import type {
  Commonv1Value,
  TypeDateTime,
  TypeTimeOfDay,
  V1Dictionary,
  V1HashableValue,
} from '@/generated/hooks/grimoire/models';
import type {
  Rainv1ListValue,
  Rainv1Value,
  V1DateTimeValue,
  V1DateValue,
  V1DurationValue,
  V1ObjectValue,
  V1TimeValue,
} from '@/generated/hooks/rain/models';
import { decodeNumber } from '@/modules/outputs/utils';
import { formatDateISO } from '@/shared/utils/datetime-utils';
import { parseFileResourceName } from '@/shared/utils/file-utils';

export type TRainValueJs =
  | string
  | number
  | boolean
  | null
  | V1DateValue
  | V1TimeValue
  | V1DateTimeValue
  | V1DurationValue
  | TRainValueJs[]
  | { [key: string]: TRainValueJs };

const objectFieldsToJs = (
  object: V1ObjectValue | undefined,
): { [key: string]: TRainValueJs } => {
  const fields = object?.fields ?? {};
  const result: { [key: string]: TRainValueJs } = {};
  for (const [key, value] of Object.entries(fields)) {
    result[key] = rainv1ValueToJs(value) ?? null;
  }
  return result;
};

const listValuesToJs = (list: Rainv1ListValue | undefined): TRainValueJs[] =>
  (list?.values ?? []).map(value => rainv1ValueToJs(value) ?? null);

/**
 * Decode a Rain polymorphic `Rainv1Value` into a plain JS value. Returns `null`
 * for the explicit null discriminant or an unrecognized empty envelope, so
 * callers can use `value ?? fallback`.
 */
export function rainv1ValueToJs(
  value: Rainv1Value | undefined,
): TRainValueJs | undefined {
  if (!value) return undefined;
  if (value.string_value !== undefined) return value.string_value;
  if (value.number_value !== undefined) return value.number_value;
  if (value.integer_value !== undefined) return value.integer_value;
  if (value.bool_value !== undefined) return value.bool_value;
  if (value.binary_value !== undefined) return value.binary_value;
  // Flatten artifact references to a display string (mirrors Rain's
  // `toJsonValue`): a binary renders as its filename, a URI reference as its
  // URI. This keeps the status-template formatter dumb and the args/result
  // panes readable instead of dumping the `{ uri, mime_type, ... }` envelope.
  if (value.artifact_value !== undefined)
    return value.artifact_value.filename ?? '<artifact>';
  if (value.artifact_uri_value !== undefined)
    return value.artifact_uri_value.uri ?? null;
  if (value.date_value !== undefined) return value.date_value;
  if (value.time_value !== undefined) return value.time_value;
  if (value.date_time_value !== undefined) return value.date_time_value;
  if (value.duration_value !== undefined) return value.duration_value;
  if (value.list_value !== undefined) return listValuesToJs(value.list_value);
  if (value.object_value !== undefined)
    return objectFieldsToJs(value.object_value);
  if (value.null_value !== undefined) return null;
  return undefined;
}

/**
 * Pull an artifact URI from a tool_result `content` payload. The artifact-
 * write/edit tools shape `content` as an `object_value` with a `uri` field of
 * type `artifact_uri_value`; since `rainv1ValueToJs` flattens artifact_uri to
 * its URI string, decoding yields `{ uri: string }`. We also accept a bare
 * `artifact_uri_value` (decoded directly to a URI string) in case a tool
 * returns one at the top level.
 */
export const extractArtifactUri = (
  value: Rainv1Value | undefined,
): string | undefined => {
  // A bare artifact_uri_value: read the URI off the discriminant directly.
  // (Decoding flattens it to a plain string, indistinguishable from a regular
  // string_value, so we must inspect the raw value here.)
  if (value?.artifact_uri_value?.uri) return value.artifact_uri_value.uri;
  const decoded = rainv1ValueToJs(value);
  if (
    typeof decoded !== 'object' ||
    decoded === null ||
    Array.isArray(decoded)
  ) {
    return undefined;
  }
  const inner = (decoded as { uri?: TRainValueJs }).uri;
  if (typeof inner === 'string') return inner;
  if (
    typeof inner === 'object' &&
    inner !== null &&
    !Array.isArray(inner) &&
    typeof (inner as { uri?: TRainValueJs }).uri === 'string'
  ) {
    return (inner as { uri: string }).uri;
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Commonv1Value (Grimoire) → Rainv1Value (Rain) conversion
// ---------------------------------------------------------------------------
// Used when sending tool calls to Rain (e.g. Quill's `create_run`) whose
// arguments originate from a Grimoire-shaped input sheet. Two related but
// distinct proto schemas; the variants overlap semantically but use different
// field names, so we translate field-by-field.

// Quill's `create_run` tool advertises `inputs` as a
// Dictionary<String, Enum{variants}>, where every value is a *tagged union*
// (TaggedValue): a single-key object whose key is the variant name and whose
// value is the inner payload — e.g. `{ "number": 42 }`, `{ "text": "Acme" }`,
// `{ "date": "2026-03-14" }`. Sending a bare scalar (which is what a native
// `Rainv1Value` like `{ number_value: 42 }` decodes to) fails validation with
// "expected one of the enum variants, got <type>". So each value is wrapped as
// a Rain object_value `{ <variant>: <inner> }`, and temporal/file payloads are
// encoded as the strings Quill expects rather than structured sub-values.

// Quill TaggedValue variant names (differ from the Commonv1Value field names).
const QUILL_VARIANT = {
  text: 'text',
  number: 'number',
  boolean: 'boolean',
  null: 'null',
  date: 'date',
  timeofday: 'timeofday',
  datetime: 'datetime',
  duration: 'duration',
  timezone: 'timezone',
  list: 'list',
  set: 'set',
  dictionary: 'dictionary',
  file: 'file',
  table: 'table',
} as const;

// `{"null": {}}` — the null variant carries an empty object.
const RAIN_EMPTY_OBJECT: Rainv1Value = { object_value: { fields: {} } };

/** Wrap an inner value as a Quill TaggedValue: `{ object_value: { <variant>: inner } }`. */
const tagged = (variant: string, inner: Rainv1Value): Rainv1Value => ({
  object_value: { fields: { [variant]: inner } },
});

const dateTimeToRfc3339 = (dt: TypeDateTime): string => {
  const year = String(dt.year ?? 1970).padStart(4, '0');
  const month = String(dt.month ?? 1).padStart(2, '0');
  const day = String(dt.day ?? 1).padStart(2, '0');
  const hours = String(dt.hours ?? 0).padStart(2, '0');
  const minutes = String(dt.minutes ?? 0).padStart(2, '0');
  const seconds = String(dt.seconds ?? 0).padStart(2, '0');
  let suffix = '';
  if (dt.nanos && dt.nanos > 0) {
    suffix = `.${String(dt.nanos).padStart(9, '0').replace(/0+$/, '')}`;
  }
  // utc_offset is a Duration string ("3600s") if set; otherwise assume Z.
  const offset = dt.utc_offset
    ? formatUtcOffsetFromSeconds(Number(dt.utc_offset.replace(/s$/, '')) || 0)
    : 'Z';
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${suffix}${offset}`;
};

const formatUtcOffsetFromSeconds = (totalSeconds: number): string => {
  if (totalSeconds === 0) return 'Z';
  const sign = totalSeconds >= 0 ? '+' : '-';
  const abs = Math.abs(totalSeconds);
  const hh = String(Math.floor(abs / 3600)).padStart(2, '0');
  const mm = String(Math.floor((abs % 3600) / 60)).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

// Quill expects `timeofday` as "HH:MM:SS[.fff]".
const timeOfDayToString = (t: TypeTimeOfDay): string => {
  const hh = String(t.hours ?? 0).padStart(2, '0');
  const mm = String(t.minutes ?? 0).padStart(2, '0');
  const ss = String(t.seconds ?? 0).padStart(2, '0');
  let suffix = '';
  if (t.nanos && t.nanos > 0) {
    suffix = `.${String(t.nanos).padStart(9, '0').replace(/0+$/, '')}`;
  }
  return `${hh}:${mm}:${ss}${suffix}`;
};

/**
 * A `Commonv1Value.file`/`.table`'s `remote` is the file's full AIP resource
 * name (`organizations/{org}/workspaces/{ws}/files/{id}--{name}.{ext}`), the
 * form the files-service upload endpoint returns. Quill/Bishop tool
 * arguments instead need the scope-free canonical `bishop://{id}--{name}.{ext}`
 * handle — already a `bishop://` URI is passed through as-is (some callers
 * may already carry one), and an unparseable value falls back to the raw
 * string rather than dropping it silently.
 */
const toBishopUri = (remote: string): string => {
  if (remote.startsWith('bishop://')) return remote;
  const parsed = parseFileResourceName(remote);
  return parsed ? `bishop://${parsed.file}` : remote;
};

const hashableKeyToString = (key: V1HashableValue | undefined): string => {
  if (!key) return '';
  if (key.text !== undefined) return key.text;
  if (key.text_value?.inline !== undefined) return key.text_value.inline;
  if (key.bool_value !== undefined) return String(key.bool_value);
  if (key.number !== undefined) return String(decodeNumber(key.number));
  if (key.date !== undefined) return formatDateISO(key.date);
  if (key.duration !== undefined) return key.duration;
  // Other hashable types are rare for sheet-produced dictionary keys; stringify
  // the JSON envelope rather than dropping the entry silently.
  return JSON.stringify(key);
};

const dictionaryToRainObject = (dict: V1Dictionary): Rainv1Value => {
  const fields: { [key: string]: Rainv1Value } = {};
  for (const entry of dict.entries ?? []) {
    if (entry.key === undefined || entry.value === undefined) continue;
    fields[hashableKeyToString(entry.key)] = commonValueToRainTaggedValue(
      entry.value,
    );
  }
  return { object_value: { fields } };
};

/**
 * Convert a Grimoire `Commonv1Value` into the Quill TaggedValue envelope
 * encoded as a Rain `Rainv1Value`. An empty envelope (no field set) becomes the
 * `null` variant so the tool call still carries a defined argument for the slot.
 */
export function commonValueToRainTaggedValue(
  value: Commonv1Value,
): Rainv1Value {
  // Primitives
  if (value.text !== undefined)
    return tagged(QUILL_VARIANT.text, { string_value: value.text });
  if (value.text_value?.inline !== undefined)
    return tagged(QUILL_VARIANT.text, {
      string_value: value.text_value.inline,
    });
  if (value.bool_value !== undefined)
    return tagged(QUILL_VARIANT.boolean, { bool_value: value.bool_value });
  if (value.null_value !== undefined)
    return tagged(QUILL_VARIANT.null, RAIN_EMPTY_OBJECT);
  if (value.number !== undefined)
    return tagged(QUILL_VARIANT.number, {
      number_value: decodeNumber(value.number),
    });

  // Temporal — Quill takes these as strings.
  if (value.date !== undefined)
    return tagged(QUILL_VARIANT.date, {
      string_value: formatDateISO(value.date),
    });
  if (value.timeofday !== undefined)
    return tagged(QUILL_VARIANT.timeofday, {
      string_value: timeOfDayToString(value.timeofday),
    });
  if (value.datetime !== undefined)
    return tagged(QUILL_VARIANT.datetime, {
      string_value: dateTimeToRfc3339(value.datetime),
    });
  if (value.duration !== undefined)
    return tagged(QUILL_VARIANT.duration, { string_value: value.duration });
  if (value.time_zone?.id !== undefined)
    return tagged(QUILL_VARIANT.timezone, { string_value: value.time_zone.id });

  // Collections — each element is itself a TaggedValue.
  if (value.list !== undefined)
    return tagged(QUILL_VARIANT.list, {
      list_value: {
        values: (value.list.items ?? []).map(commonValueToRainTaggedValue),
      },
    });
  if (value.list_value?.inline?.items !== undefined)
    return tagged(QUILL_VARIANT.list, {
      list_value: {
        values: value.list_value.inline.items.map(commonValueToRainTaggedValue),
      },
    });
  if (value.set !== undefined)
    return tagged(QUILL_VARIANT.set, {
      list_value: {
        values: (value.set.items ?? []).map(item =>
          commonValueToRainTaggedValue(item as Commonv1Value),
        ),
      },
    });
  if (value.set_value?.inline?.items !== undefined)
    return tagged(QUILL_VARIANT.set, {
      list_value: {
        values: value.set_value.inline.items.map(item =>
          commonValueToRainTaggedValue(item as Commonv1Value),
        ),
      },
    });
  if (value.dictionary !== undefined)
    return tagged(
      QUILL_VARIANT.dictionary,
      dictionaryToRainObject(value.dictionary),
    );
  if (value.dictionary_value?.inline !== undefined)
    return tagged(
      QUILL_VARIANT.dictionary,
      dictionaryToRainObject(value.dictionary_value.inline),
    );

  // Artifacts — Quill takes a canonical `bishop://{id}--{name}.{ext}` handle,
  // not the files-service resource name `value.file.remote` carries.
  if (value.file !== undefined)
    return tagged(QUILL_VARIANT.file, {
      string_value: value.file.remote ? toBishopUri(value.file.remote) : '',
    });
  if (value.table !== undefined)
    return tagged(QUILL_VARIANT.table, {
      string_value: value.table.remote ? toBishopUri(value.table.remote) : '',
    });

  // Empty / unset → null. Sheet may produce an empty {} for optional unset slots.
  return tagged(QUILL_VARIANT.null, RAIN_EMPTY_OBJECT);
}

/**
 * Wraps a Grimoire-shaped input record into the single Rain `Rainv1Value` that
 * Quill's `create_run` tool expects under its `inputs` argument: a dictionary
 * (encoded as a Rain `object_value`) whose fields each carry a TaggedValue.
 */
export function commonValuesToRainInputsArgument(
  values: Record<string, Commonv1Value>,
): Rainv1Value {
  const fields: { [key: string]: Rainv1Value } = {};
  for (const [key, value] of Object.entries(values)) {
    fields[key] = commonValueToRainTaggedValue(value);
  }
  return { object_value: { fields } };
}
